import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Download, Play, ShieldCheck, Flame, Sparkles, Award, Gift, DollarSign, Code2, Zap } from "lucide-react";
import { api, fetchHome, fetchCategories, fetchFeaturedSlots, formatDownloads } from "@/lib/api";
import { AppCard, AppCardCompact } from "@/components/AppCard";
import SectionHeader from "@/components/SectionHeader";
import Footer from "@/components/Footer";
import { HOME, CATEGORY_CARD, FEATURED } from "@/constants/testIds";

const iconMap = {
  "gamepad-2": "🎮", briefcase: "💼", "graduation-cap": "🎓", music: "🎵",
  "heart-pulse": "❤️", "trending-up": "📈", zap: "⚡", camera: "📷",
  users: "👥", plane: "✈️", "brain-circuit": "🧠", wrench: "🔧",
  "shopping-bag": "🛍️", sparkles: "✨", film: "🎬", shield: "🛡️",
};

import { Showcase } from "@/components/home/Showcase";
import { TopApps, HomeSections } from "@/components/home/TopApps";
import { LauncherWidget } from "@/components/home/Launcher";
import { api as _hapi } from "@/lib/api";

export default function Home() {
  const [sections, setSections] = useState(null);
  const [feed, setFeed] = useState(null);
  const [cats, setCats] = useState([]);
  const [boosted, setBoosted] = useState([]);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    setFeed(null);
    fetchHome().then(setFeed).catch((e) => {
      setError(e?.message || "Couldn't reach Jixxeo. Check your connection and try again.");
    });
    fetchCategories().then(setCats).catch(() => {});
    fetchFeaturedSlots().then((d) => setBoosted(d.apps || [])).catch(() => {});
    _hapi.get("/home/sections").then((r) => setSections(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (error && !feed) return <LoadingHome error={error} onRetry={load} />;
  if (!feed) return <LoadingHome />;
  return (
    <>
      <Hero feed={feed} />
      <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-10 pt-8 text-white" data-testid="home-upgrade">
        <Showcase apps={sections?.featured} />
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-5 items-start"><TopApps /><LauncherWidget /></div>
        <HomeSections data={sections} />
      </div>
      <div className="max-w-7xl mx-auto px-6 space-y-24 pb-16 pt-16">
        <PowerBanner />
        <EarnBanner />
        {boosted.length > 0 && <BoostedSection apps={boosted} />}
        <TrendingSection apps={feed.trending} />
        <CategoriesSection cats={cats} />
        <EditorsChoiceSection apps={feed.editors_choice} />
        <TopChartsSection topFree={feed.top_free} topPaid={feed.top_paid} openSrc={feed.open_source} />
        <NewReleasesSection apps={feed.new_releases} />
        <CTABand />
      </div>
      <Footer />
    </>
  );
}

function PowerBanner() {
  return (
    <section data-testid="home-power-banner" className="relative -mt-8">
      <Link to="/power" className="block group" data-testid="home-power-cta">
        <div className="relative overflow-hidden rounded-3xl border border-[#1E88FF]/40 bg-gradient-to-br from-[#1E88FF]/20 via-[#0A1A2F]/40 to-black p-6 md:p-8">
          <div className="absolute inset-0 pointer-events-none opacity-50" style={{
            background: "radial-gradient(ellipse at 85% 15%, rgba(30,136,255,0.35), transparent 60%), radial-gradient(ellipse at 15% 85%, rgba(0,240,255,0.15), transparent 60%)",
          }} />
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{
            backgroundImage: "linear-gradient(rgba(30,136,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(30,136,255,0.2) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] flex items-center justify-center shadow-2xl shadow-[#1E88FF]/40">
              <Zap className="w-8 h-8 md:w-10 md:h-10 text-white" fill="white" />
            </div>
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#1E88FF] flex items-center gap-2 mb-3">
                <Zap className="w-3 h-3" fill="currentColor" /> // new app · petitions that move
              </div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                Jixxeo <span className="text-[#1E88FF]">Power</span>
              </h2>
              <p className="text-white/70 mt-4 max-w-xl text-base md:text-lg">
                Start petitions, sign the causes you care about, and earn rewards for every action. Legal, mobile-first, and yours to share.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2 mono text-[10px] uppercase tracking-widest">
                <span className="px-3 py-1.5 rounded-full border border-[#1E88FF]/40 bg-[#1E88FF]/10 text-[#1E88FF]">20 categories</span>
                <span className="px-3 py-1.5 rounded-full border border-white/15 text-white/70">Trending & victories</span>
                <span className="px-3 py-1.5 rounded-full border border-white/15 text-white/70">Rewards wallet</span>
                <span className="px-3 py-1.5 rounded-full border border-[#1E88FF]/40 bg-[#1E88FF]/10 text-[#1E88FF]">PWA · install to home</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <span className="inline-flex items-center gap-2 bg-[#1E88FF] group-hover:bg-white text-white group-hover:text-black font-black mono text-xs uppercase tracking-widest px-6 py-3.5 rounded-full transition-colors">
                <Zap className="w-4 h-4" fill="currentColor" /> Open Jixxeo Power <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="mono text-[9px] uppercase tracking-widest text-white/40 text-center">free · signs in with your account</span>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
}

function EarnBanner() {
  return (
    <section data-testid="home-earn-banner" className="relative -mt-8">
      <Link to="/earn" className="block group">
        <div className="relative overflow-hidden rounded-3xl border border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/15 via-[#00F0FF]/5 to-[#FFE300]/10 p-6 md:p-8">
          {/* backdrop */}
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{
            background: "radial-gradient(ellipse at 90% 10%, rgba(57,255,20,0.3), transparent 60%), radial-gradient(ellipse at 10% 90%, rgba(255,227,0,0.2), transparent 60%)",
          }} />
          <div className="absolute inset-0 pointer-events-none opacity-30" style={{
            backgroundImage: "linear-gradient(rgba(57,255,20,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.15) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] flex items-center gap-2 mb-3">
                <DollarSign className="w-3 h-3" /> // members only · never forced
              </div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                Earn <span className="text-[#39FF14]">real money</span><br /> playing apps
              </h2>
              <p className="text-white/70 mt-4 max-w-xl text-base md:text-lg">
                No ads, ever. Earn from app play time, daily launch streaks, Trivia wins and verified Trash Hunter cleanups. Watch your wallet fill up and cash out at <strong className="text-white">$5</strong> — flat <strong className="text-[#FFE300]">$0.10 fee</strong> per payout.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2 mono text-[10px] uppercase tracking-widest">
                <span className="px-3 py-1.5 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]">100% ad-free</span>
                <span className="px-3 py-1.5 rounded-full border border-white/15 text-white/70">$5 cash-out</span>
                <span className="px-3 py-1.5 rounded-full border border-[#FFE300]/40 bg-[#FFE300]/10 text-[#FFE300]">$0.10 flat cashout fee</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <span className="inline-flex items-center gap-2 bg-[#39FF14] group-hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-6 py-3.5 rounded-full transition-colors">
                <DollarSign className="w-4 h-4" /> Start earning <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="mono text-[9px] uppercase tracking-widest text-white/40 text-center">PayPal · Venmo · Cash App</span>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
}

function BoostedSection({ apps }) {
  return (
    <section data-testid={FEATURED.home}>
      <SectionHeader
        eyebrow="// boosted by creators"
        title="Featured Slots"
        action={<span className="mono text-[10px] uppercase tracking-widest text-[#FFE300] border border-[#FFE300]/40 bg-[#FFE300]/10 px-3 py-1 rounded-full">Paid Placement</span>}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.slice(0, 6).map((a) => (
          <Link key={a.slug} to={`/app/${a.slug}`} className="relative rounded-2xl overflow-hidden group border border-[#FFE300]/30 hover:border-[#FFE300] transition-colors">
            <div className="aspect-[16/9] relative">
              {a.screenshots?.[0] && <img src={a.screenshots[0]} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
              <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#FFE300] text-black font-bold">
                ★ Boosted
              </div>
            </div>
            <div className="p-4 flex items-center gap-3 relative -mt-8">
              {a.icon_url && <img src={a.icon_url} className="w-14 h-14 rounded-xl ring-2 ring-[#050505]" alt="" />}
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-base truncate">{a.name}</div>
                <div className="mono text-[10px] text-white/50">{a.developer}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function LoadingHome({ error, onRetry }) {
  if (error) {
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="home-error">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FF003C] mb-3">// connection lost</div>
        <div className="font-heading font-black text-3xl uppercase tracking-tighter mb-2">Can't reach Jixxeo</div>
        <div className="text-white/60 text-sm mb-6">{error}</div>
        <button onClick={onRetry} data-testid="home-error-retry"
          className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full">
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className="max-w-7xl mx-auto px-6 py-20">
      <div className="mono text-[#00F0FF] text-sm animate-pulse">Loading Jixxeo…</div>
    </div>
  );
}

function Hero({ feed }) {
  const [big, ...rest] = feed.featured;
  if (!big) return null;
  return (
    <section data-testid={HOME.hero} className="relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-20 relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="mono text-[10px] uppercase tracking-[0.35em] text-[#00F0FF] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#00F0FF]" />
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9]">
            Discover.<br />
            <span className="text-[#00F0FF] text-glow-cyan">Publish.</span> Launch.<br />
            <span className="text-white/30">Without limits.</span>
          </h1>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/feed" className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full transition-colors pulse-neon">
              <span>▶ Enter the Feed</span>
            </Link>
            <Link to="/browse" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00F0FF]/60 hover:text-[#00F0FF] text-white/80 mono text-xs uppercase tracking-widest px-5 py-3 rounded-full transition-colors">
              Browse All Apps
            </Link>
          </div>
        </motion.div>

        {/* Bento hero grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:h-[520px]">
          {/* Big featured */}
          <FeaturedBig app={big} />

          <div className="flex flex-col gap-4 lg:h-full">
            {rest.slice(0, 2).map((a, i) => (
              <FeaturedSmall key={a.slug} app={a} idx={i} />
            ))}
          </div>
        </div>

        <StatStrip />
      </div>
    </section>
  );
}

function StatStrip() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/platform/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="home-stats">
      {[
        { k: "APPS", v: stats?.apps },
        { k: "DEVS", v: stats?.devs },
        { k: "LAUNCHES", v: stats?.launches },
        { k: "REVIEWS", v: stats?.reviews },
      ].map((s) => (
        <div key={s.k} className="glass rounded-xl p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-white/40">{s.k}</div>
          <div className="font-heading font-black text-2xl mt-1" data-testid={`home-stat-${s.k.toLowerCase()}`}>
            {stats ? fmt(s.v) : <span className="inline-block w-12 h-6 rounded bg-white/10 animate-pulse align-middle" />}
          </div>
        </div>
      ))}
    </div>
  );
}

function FeaturedBig({ app }) {
  return (
    <Link
      to={`/app/${app.slug}`}
      data-testid={HOME.featured}
      className="lg:col-span-2 relative rounded-3xl overflow-hidden group h-[420px] lg:h-full border border-white/10 hover:border-[#00F0FF]/40 transition-colors"
    >
      <img src={app.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-[opacity,transform] duration-700" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-[#050505]/70 to-transparent" />
      <div className="absolute inset-0 p-8 flex flex-col justify-between">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
          <Flame className="w-3.5 h-3.5 text-[#FF003C]" />
          <span className="text-[#FF003C]">FEATURED · TIER 1 DROP</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-3">
            <img src={app.icon_url} className="w-16 h-16 rounded-2xl ring-2 ring-white/20" alt="" />
            <div>
              <div className="mono text-xs text-white/60">{app.developer}</div>
              <div className="flex items-center gap-2">
                {app.developer_verified && <ShieldCheck className="w-3.5 h-3.5 text-[#00F0FF]" />}
                <span className="mono text-[10px] uppercase text-white/40">Verified</span>
              </div>
            </div>
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter mb-2">
            {app.name}
          </h2>
          <p className="text-white/70 max-w-lg">{app.tagline}</p>
          <div className="flex items-center gap-4 mt-4 mono text-xs">
            <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#FFE300] text-[#FFE300]" /> {app.rating.toFixed(1)}</span>
            <span className="flex items-center gap-1" title="Launches"><Play className="w-3.5 h-3.5" /> {formatDownloads(app.downloads)} launches</span>
            <span className="text-white/40">·</span>
            <span>{app.file_size_mb} MB</span>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 bg-[#00F0FF] text-black px-5 py-3 rounded-full font-bold mono text-xs uppercase tracking-widest group-hover:bg-white transition-colors">
            Open App <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function FeaturedSmall({ app, idx }) {
  const accent = idx === 0 ? "#39FF14" : "#FFE300";
  return (
    <Link
      to={`/app/${app.slug}`}
      className="relative rounded-3xl overflow-hidden group flex-1 border border-white/10 hover:border-[#00F0FF]/40 transition-colors"
    >
      <img src={app.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
      <div className="relative p-5 h-full flex flex-col justify-between min-h-[190px]">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest" style={{ color: accent }}>
          <Sparkles className="w-3 h-3" />
          <span>EDITOR SPOTLIGHT</span>
        </div>
        <div>
          <div className="flex items-center gap-3">
            <img src={app.icon_url} className="w-12 h-12 rounded-xl ring-1 ring-white/20" alt="" />
            <div>
              <h3 className="font-heading font-bold text-lg leading-tight">{app.name}</h3>
              <div className="mono text-[10px] text-white/50">{app.developer}</div>
            </div>
          </div>
          <p className="text-sm text-white/60 mt-2">{app.tagline}</p>
        </div>
      </div>
    </Link>
  );
}

function TrendingSection({ apps }) {
  return (
    <section data-testid={HOME.trending}>
      <SectionHeader
        eyebrow="// heating up"
        title="Trending Now"
        action={
          <Link to="/browse?sort=trending" className="mono text-xs uppercase tracking-widest text-white/60 hover:text-[#00F0FF] flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {apps.slice(0, 8).map((a, i) => (
          <motion.div
            key={a.slug}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
          >
            <AppCard app={a} />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CategoriesSection({ cats }) {
  return (
    <section data-testid={HOME.categories}>
      <SectionHeader eyebrow="// explore the grid" title="Categories" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {cats.map((c) => (
          <Link
            key={c.id}
            to={`/category/${c.id}`}
            data-testid={`${CATEGORY_CARD}-${c.id}`}
            className="glass rounded-xl p-4 group hover:border-[#00F0FF]/60 hover:-translate-y-1 transition-[transform,border-color] duration-300"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 text-xl"
              style={{ background: `${c.color}15`, border: `1px solid ${c.color}30` }}
            >
              {iconMap[c.icon] || "◆"}
            </div>
            <div className="font-heading font-bold text-sm">{c.name}</div>
            <div className="mono text-[10px] text-white/40 mt-0.5">{c.count} apps</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function EditorsChoiceSection({ apps }) {
  return (
    <section data-testid={HOME.editorsChoice}>
      <SectionHeader
        eyebrow="// hand-picked by humans"
        title="Editor's Choice"
        action={<Award className="w-6 h-6 text-[#FFE300]" />}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.slice(0, 6).map((a) => (
          <Link
            key={a.slug}
            to={`/app/${a.slug}`}
            className="relative rounded-2xl overflow-hidden group border border-white/10 hover:border-[#FFE300]/50 transition-colors"
          >
            <div className="aspect-[16/10] relative">
              <img src={a.screenshots[0]} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
              <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#FFE300]/15 text-[#FFE300] border border-[#FFE300]/30">
                Editor&apos;s Pick
              </div>
            </div>
            <div className="p-4 flex items-center gap-3 relative -mt-8">
              <img src={a.icon_url} className="w-14 h-14 rounded-xl ring-2 ring-[#050505]" alt="" />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-base truncate">{a.name}</div>
                <div className="mono text-[10px] text-white/50">{a.developer}</div>
              </div>
              <span className="mono text-xs flex items-center gap-1">
                <Star className="w-3 h-3 fill-[#FFE300] text-[#FFE300]" />{a.rating.toFixed(1)}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function TopChartsSection({ topFree, topPaid, openSrc }) {
  const cols = [
    { title: "Top Free", icon: <Gift className="w-5 h-5 text-[#39FF14]" />, apps: topFree, tid: HOME.topFree, accent: "#39FF14" },
    { title: "Top Paid", icon: <DollarSign className="w-5 h-5 text-[#FFE300]" />, apps: topPaid, tid: HOME.topPaid, accent: "#FFE300" },
    { title: "Open Source", icon: <Code2 className="w-5 h-5 text-[#00F0FF]" />, apps: openSrc, tid: HOME.openSource, accent: "#00F0FF" },
  ];
  return (
    <section>
      <SectionHeader eyebrow="// leaderboard" title="Top Charts" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cols.map((col) => (
          <div key={col.title} data-testid={col.tid} className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              {col.icon}
              <h3 className="font-heading font-bold text-lg">{col.title}</h3>
            </div>
            <div className="space-y-1">
              {col.apps.slice(0, 5).map((a, i) => (
                <AppCardCompact key={a.slug} app={a} index={i} />
              ))}
              {col.apps.length === 0 && (
                <div className="mono text-xs text-white/40 py-6 text-center">No entries yet</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NewReleasesSection({ apps }) {
  return (
    <section data-testid={HOME.newReleases}>
      <SectionHeader
        eyebrow="// fresh drops"
        title="New Releases"
        action={
          <Link to="/browse?sort=new" className="mono text-xs uppercase tracking-widest text-white/60 hover:text-[#00F0FF] flex items-center gap-1">
            All new <ArrowRight className="w-3 h-3" />
          </Link>
        }
      />
      <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-4 -mx-6 px-6 snap-x">
        {apps.map((a) => (
          <Link
            key={a.slug}
            to={`/app/${a.slug}`}
            className="snap-start w-64 flex-shrink-0 group"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[3/4] border border-white/10 group-hover:border-[#00F0FF]/50 transition-colors">
              <img src={a.screenshots[1] || a.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest bg-[#FF003C]/20 text-[#FF003C] px-2 py-1 rounded-full border border-[#FF003C]/40">
                NEW
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <img src={a.icon_url} className="w-10 h-10 rounded-lg ring-1 ring-white/20" alt="" />
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-base truncate">{a.name}</div>
                    <div className="mono text-[10px] text-white/50">v{a.version}</div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function CTABand() {
  return (
    <section className="relative rounded-3xl overflow-hidden border border-[#00F0FF]/20 p-8 md:p-12 glass grid-bg">
      <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// developer console</div>
          <h3 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">
            Ship your app to the world
          </h3>
          <p className="text-white/60 mt-2 max-w-md">
            No gatekeepers. No 30% cuts. Publish APKs, ship updates, own your revenue.
            <span className="text-white/40"> (Console — Phase 2)</span>
          </p>
        </div>
        <button className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full hover:bg-white transition-colors pulse-neon">
          <Zap className="w-4 h-4" /> Join Waitlist
        </button>
      </div>
    </section>
  );
}
